import React, { useMemo, useState } from 'react';
import {
  MapPin, Package, CheckCircle, AlertTriangle, BarChart3, ChevronRight, Search, Grid3X3, HelpCircle, X
} from 'lucide-react';
import { FieldRegistrationSummary } from '../../../../types/fieldRegistration';
import { RackSummary } from '../rack-builder/types';

interface OverviewPanelProps {
  summary: FieldRegistrationSummary | null;
  rackSummaries: RackSummary[];
  totalBins: number;
  onStartRegistration: () => void;
  onSelectRack: (prefix: string) => void;
}

/**
 * Small inline help icon that shows/hides a description on tap
 */
const HelpTip: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);

  return (
    <span className="relative inline-flex ml-1">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="text-gray-400 hover:text-gray-600 focus:outline-none"
        aria-label="Hjelp"
      >
        <HelpCircle size={13} />
      </button>
      {open && (
        <>
          {/* Backdrop for mobile tap-away */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-50 bg-gray-800 text-white text-xs rounded-lg p-2.5 shadow-lg w-52 leading-relaxed">
            {text}
            <button
              onClick={() => setOpen(false)}
              className="absolute top-1 right-1 text-gray-400 hover:text-white"
              aria-label="Lukk"
            >
              <X size={12} />
            </button>
          </div>
        </>
      )}
    </span>
  );
};

export const OverviewPanel: React.FC<OverviewPanelProps> = ({
  summary,
  rackSummaries,
  totalBins,
  onStartRegistration,
  onSelectRack,
}) => {
  const overallPercent = summary
    ? Math.round((summary.completedLocations / Math.max(summary.totalLocations, 1)) * 100)
    : 0;

  // Calculate how many locations belong to special/named bins (not in any rack)
  const rackLocationCount = useMemo(
    () => rackSummaries.reduce((sum, r) => sum + r.locationCount, 0),
    [rackSummaries]
  );
  const specialLocationCount = totalBins - rackLocationCount;

  return (
    <div className="space-y-4">
      {/* Progress Summary Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 size={16} className="text-[#0E7C86]" />
            Fremdrift
          </h3>
          <span className="text-xs text-gray-500">
            {summary?.completedLocations || 0} / {summary?.totalLocations || totalBins} lokasjoner
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
          <div
            className="bg-[#0E7C86] h-3 rounded-full transition-all duration-500"
            style={{ width: `${overallPercent}%` }}
          />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <MapPin size={14} className="text-[#0E7C86]" />
              <span className="text-xs text-gray-500">Lokasjoner</span>
              <HelpTip text="Totalt antall lagerplasser (hyller/binger) i dette lageret." />
            </div>
            <p className="text-lg font-bold text-gray-900">{summary?.totalLocations || totalBins}</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Package size={14} className="text-[#F58220]" />
              <span className="text-xs text-gray-500">Varer totalt</span>
              <HelpTip text="Antall unike varenumre som er registrert med beholdning i lageret." />
            </div>
            <p className="text-lg font-bold text-gray-900">{summary?.totalItems || 0}</p>
          </div>

          <div className="bg-green-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle size={14} className="text-green-600" />
              <span className="text-xs text-gray-500">Kapasitet OK</span>
              <HelpTip text="Varer som har fått definert maks-kapasitet (maks antall per lokasjon). Disse er ferdig konfigurert." />
            </div>
            <p className="text-lg font-bold text-green-700">{summary?.itemsWithCapacity || 0}</p>
          </div>

          <div className="bg-amber-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={14} className="text-amber-600" />
              <span className="text-xs text-gray-500">Mangler kapasitet</span>
              <HelpTip text="Varer som ennå ikke har fått definert maks-kapasitet. Disse bør konfigureres for optimal lagerstyring." />
            </div>
            <p className="text-lg font-bold text-amber-700">{summary?.itemsMissingCapacity || 0}</p>
          </div>
        </div>
      </div>

      {/* Quick Start Button */}
      <button
        onClick={onStartRegistration}
        className="w-full bg-[#0E7C86] hover:bg-[#0A5F66] text-white font-semibold py-4 px-4 rounded-lg transition-colors min-h-[52px] flex items-center justify-center gap-2 active:scale-[0.98]"
      >
        <MapPin size={20} />
        Start feltregistrering
      </button>

      {/* Rack Quick-access */}
      {rackSummaries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Grid3X3 size={16} className="text-[#0E7C86]" />
              Velg reol
            </h3>
            <span className="text-xs text-gray-500">
              {rackSummaries.length} reoler
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {rackSummaries.map((rack) => (
              <button
                key={rack.rackNumber}
                onClick={() => onSelectRack(rack.rackNumber)}
                className="bg-gray-50 hover:bg-[#E0F2F4] border border-gray-200 hover:border-[#0E7C86] rounded-lg p-3 text-left transition-colors min-h-[44px] active:scale-95"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">
                    Reol {rack.rackNumber}
                  </span>
                  <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-[#0E7C86] font-medium">
                    {rack.locationCount} lok.
                  </span>
                  {rack.maxVertical && rack.maxShelf && (
                    <span className="text-xs text-gray-400">
                      V{rack.minVertical}-{rack.maxVertical} &times; H{rack.minShelf}-{rack.maxShelf}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Special locations info */}
          {specialLocationCount > 0 && (
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-2.5">
              <Search size={12} className="text-gray-400 flex-shrink-0" />
              <span>
                {specialLocationCount} spesiallokasjon{specialLocationCount !== 1 ? 'er' : ''} (MOTTAK, LAGER m.fl.)
                — bruk sok eller Lokasjoner-fanen
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
